import {inject, Injectable} from '@angular/core';
import {OperationService} from './operation.service';
import {OperationStep} from '../core/definitions/operation-step';

/**
 * Each finished job's next step, asked of the backend once per job for
 * the session. A job whose step is complete gives the same answer however
 * often it is asked - `null` for a job past its last step - and the
 * operations panel that shows these answers is created anew each time it
 * opens, so the answers are kept here rather than on the panel.
 */
@Injectable({providedIn: 'root'})
export class FinishedJobStepsService {
  private readonly operationService = inject(OperationService);
  private readonly steps = new Map<string, OperationStep>();
  private readonly pending = new Map<string, Promise<OperationStep>>();

  /** Whether job `queueId`'s answer has arrived, whatever it was. */
  has(queueId: string): boolean {
    return this.steps.has(queueId);
  }

  /** Job `queueId`'s answer once it has arrived; `undefined` before. */
  get(queueId: string): OperationStep | undefined {
    return this.steps.get(queueId);
  }

  /** Job `queueId`'s next step: the kept answer, else the backend's. A
   * request still in flight is shared rather than repeated; a failed one
   * is not kept, so the next call asks again. */
  nextStep(queueId: string): Promise<OperationStep> {
    if (this.steps.has(queueId)) {
      return Promise.resolve(this.steps.get(queueId));
    }
    let request = this.pending.get(queueId);
    if (!request) {
      request = this.operationService.getNextOperationStepFromQueue(queueId);
      this.pending.set(queueId, request);
      request.then(step => {
        this.steps.set(queueId, step);
      }).catch(() => undefined).finally(() => this.pending.delete(queueId));
    }
    return request;
  }
}
